import type { FC, ReactNode } from 'react';
import { Users, CheckCircle2, PenLine, Crown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMembersStats } from '@/lib/api/hooks/member';
import { Loader } from '@/components/ui';

interface StatsCardProps {
	title: string;
	value: number;
	description: string;
	icon: ReactNode;
	iconContainerClassName: string;
	iconClassName: string;
}

type StatKey = 'totalMembers' | 'totalPublished' | 'totalDraft' | 'totalPresidentMembers';

interface StatConfig {
	key: StatKey;
	title: string;
	icon: LucideIcon;
	iconContainerClassName: string;
	iconClassName: string;
	getDescription: () => string;
}

const StatCard = ({
	title,
	value,
	description,
	icon,
	iconContainerClassName,
	iconClassName,
}: StatsCardProps) => {
	return (
		<div className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="flex gap-4">
				<div className={`flex h-12 w-12 items-center justify-center rounded ${iconContainerClassName}`}>
					<span className={iconClassName}>{icon}</span>
				</div>
				<div>
					<p className="text-base font-medium text-gray-600">{title}</p>
					<p className="text-[2rem] leading-[1.1] font-semibold text-gray-900">{value}</p>
					<p className="text-sm text-gray-500">{description}</p>
				</div>
			</div>
		</div>
	);
};

const STATS_CONFIG: StatConfig[] = [
	{
		key: 'totalMembers',
		title: 'Total Members',
		icon: Users,
		iconContainerClassName: 'bg-blue-50',
		iconClassName: 'text-blue-600',
		getDescription: () => 'All leadership profiles',
	},
	{
		key: 'totalPublished',
		title: 'Published',
		icon: CheckCircle2,
		iconContainerClassName: 'bg-[#E8F6EE90]',
		iconClassName: 'text-[#0F8A47]',
		getDescription: () => 'Live on website',
	},
	{
		key: 'totalDraft',
		title: 'Drafts',
		icon: PenLine,
		iconContainerClassName: 'bg-[#FDF3E690]',
		iconClassName: 'text-[#F97316]',
		getDescription: () => 'Not published yet',
	},
	{
		key: 'totalPresidentMembers',
		title: 'Presidential Council',
		icon: Crown,
		iconContainerClassName: 'bg-purple-50',
		iconClassName: 'text-purple-600',
		getDescription: () => 'Council members',
	},
];

const MembersStats: FC = () => {
	const { data: stats, isFetching, refetch, isError } = useMembersStats();

	const values: Record<StatKey, number> = {
		totalMembers: stats?.totalMembers ?? 0,
		totalPublished: stats?.totalPublished ?? 0,
		totalDraft: stats?.totalDraft ?? 0,
		totalPresidentMembers: stats?.totalPresidentMembers ?? 0,
	};

	return (
		<section className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:flex xl:flex-row xl:gap-4">
			{isError ? (
				<div className="col-span-full flex h-full w-full flex-col items-center justify-center gap-4 p-4">
					<p>Error loading statistics.</p>
					<button onClick={() => refetch()} className="rounded bg-forestGreen-100 px-4 py-2 text-white">
						Retry
					</button>
				</div>
			) : isFetching ? (
				STATS_CONFIG.map((stat) => {
					return (
						<div key={stat.key} className="flex h-full min-h-28 w-full items-center justify-center p-4">
							<Loader size="md" variant="wine" />
						</div>
					);
				})
			) : (
				STATS_CONFIG.map((stat) => {
					const Icon = stat.icon;

					return (
						<StatCard
							key={stat.key}
							title={stat.title}
							value={values[stat.key]}
							description={stat.getDescription()}
							icon={<Icon className="h-4 w-4 md:h-6 md:w-6"  />}
							iconContainerClassName={stat.iconContainerClassName}
							iconClassName={stat.iconClassName}
						/>
					);
				})
			)}
		</section>
	);
};

export default MembersStats;
